import type { NuxtApp } from '#app';
import type { PaginatedApiResponse, ProductAttributes } from '~/types';

/**
 * @param api the caller's useApi(); required so it can't be handed to useAsyncData, which passes the Nuxt app first
 */
export default async function getAllProducts(api: NuxtApp['$api']): Promise<ProductAttributes[]> {
    const loadedProducts: ProductAttributes[] = [];

    const promises = await api<PaginatedApiResponse<ProductAttributes>>('/products')
        .then(paginator => {
            loadedProducts.push(...paginator.data);

            const promises: Promise<unknown>[] = [];

            for (let i = 2; i <= paginator.meta.lastPage; i++) {
                promises.push(
                    api<PaginatedApiResponse<ProductAttributes>>(`/products?page=${i}`)
                        .then(paginator => loadedProducts.push(...paginator.data))
                );
            }

            return promises;
        });

    await Promise.all(promises);
    loadedProducts.forEach(product => {
        // ensure images are sorted by order
        product.images.sort((a, b) => a.meta.order - b.meta.order);

        // ensure variations are sorted by cost (ascending)
        product.variations.sort((a, b) => {
            return a.cost
                ? b.cost
                    ? a.cost - b.cost
                    : -1
                : 1;
        });
    });
    // order by name ascending
    loadedProducts.sort(
        (a, b) => a.name.localeCompare(b.name, 'en', { ignorePunctuation: true, sensitivity: 'accent' })
    );

    return loadedProducts;
}
